/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Zap, Plus, Trash2, Edit2, Calendar, Clock, MapPin, GripVertical, Save, X, Film, Download, Grid3X3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): SceneExpand
// was wired for the strip-board scene expander but the JSX callsite was
// dropped when SceneViewModal took over. Restore <SceneExpand.../> for the
// full-panel expand behaviour OR delete this import.
import SceneExpand from '../components/SceneExpand';
import SceneViewModal from '../components/SceneViewModal';
import { toast } from 'sonner';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── SceneGridCard — collapsed card with synopsis, notes, FIPR; click → modal ─
const SceneGridCard = ({
  scene, onOpen, onNoteSave,
  style, itemCount, synopsis, hasNote, fiprStage, fiprColor, fiprLetter,
}) => {
  const [editingSynopsis, setEditingSynopsis] = useState(false);
  const [editDraft, setEditDraft] = useState('');  // draft only active while editing
  const { token } = useAuth();

  const saveSynopsis = async () => {
    setEditingSynopsis(false);
    if (editDraft === synopsis) return;
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scenes/${scene.id}`,
        { synopsis_en: editDraft },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) { console.error('strip-board.synopsis-en-patch:', err); }
  };

  const noteText = (scene.strip_notes || '').trim();

  return (
    <div
      className="rounded-xl border-2 bg-[#1a1a1a] overflow-hidden transition-all duration-150 hover:border-[#e8c547]/50 hover:-translate-y-0.5 cursor-pointer"
      style={{ borderColor: '#2a2a2a' }}
      onClick={onOpen}
      data-testid={`scene-board-${scene.scene_number ?? 'unnumbered'}`}
    >
      {/* Top accent bar */}
      <div className="h-1 w-full" style={{ backgroundColor: style.accent }} />

      <div className="p-3">
        {/* Row 1: scene number + type pill + FIPR + note dot */}
        <div className="flex items-center justify-between mb-1.5 gap-1">
          <span className="text-base font-bold text-[#e8c547]">{formatSceneNumber(scene.scene_number, { prefix: '#' })}</span>
          <div className="flex items-center gap-1 min-w-0">
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${style.pill}`}>
              {scene.location_type} {scene.time_of_day}
            </span>
            {fiprLetter && (
              <span className="text-[9px] font-black px-1 py-px rounded shrink-0"
                    style={{ background: `${fiprColor}18`, color: fiprColor, border: `1px solid ${fiprColor}30` }}>
                {fiprLetter}
              </span>
            )}
            {hasNote && (
              <span className="h-2 w-2 rounded-full shrink-0"
                    style={{ background: '#e8c547', boxShadow: '0 0 4px rgba(232,197,71,0.6)' }}
                    title="Has notes" />
            )}
          </div>
        </div>

        {/* Row 2: scene heading */}
        <p className="text-xs font-semibold text-[#f0f0f0] line-clamp-2 mb-1 leading-tight">
          {scene.scene_header}
        </p>

        {/* Row 3: location */}
        <p className="text-[10px] text-[#606060] truncate mb-1.5">{scene.location_name}</p>

        {/* Row 4: synopsis — live from prop (reflects EN/Original toggle), editable inline */}
        {editingSynopsis ? (
          <textarea
            autoFocus
            value={editDraft}
            onChange={e => setEditDraft(e.target.value)}
            onBlur={saveSynopsis}
            onKeyDown={e => { if (e.key === 'Escape') setEditingSynopsis(false); }}
            onClick={e => e.stopPropagation()}
            rows={2}
            className="w-full text-[11px] text-[#d0d0d0] bg-[#0d0d0d] border border-[#e8c547]/30 rounded px-2 py-1 resize-none outline-none mb-1.5 leading-relaxed"
            data-testid={`synopsis-edit-${scene.scene_number ?? 'unnumbered'}`}
          />
        ) : (
          <p
            className="text-[11px] text-[#888] line-clamp-2 mb-1.5 leading-relaxed cursor-text hover:text-[#a0a0a0] transition-colors"
            onClick={e => { e.stopPropagation(); setEditDraft(synopsis); setEditingSynopsis(true); }}
            title="Click to edit synopsis"
            data-testid={`synopsis-${scene.scene_number ?? 'unnumbered'}`}
          >
            {synopsis || <span className="italic text-[#444]">No synopsis — click to add</span>}
          </p>
        )}

        {/* Row 5: note preview */}
        {hasNote && (
          <p className="text-[10px] text-[#707070] italic line-clamp-1 mb-1.5 border-l-2 border-[#e8c547]/30 pl-1.5"
             onClick={e => e.stopPropagation()}>
            {noteText}
          </p>
        )}

        {/* Row 6: stats footer */}
        <div className="flex items-center justify-between text-[10px] text-[#505050]">
          <span>{((scene.page_count_eighths || 0) / 8).toFixed(1)} pg</span>
          <span>{itemCount} items</span>
          <span>{(scene.characters_in_scene || []).length} cast</span>
        </div>
      </div>
    </div>
  );
};

// ── FIPR pill config ───────────────────────────────────────────────────────
const FIPR_CONFIG = {
  FUNNEL:         { letter: 'F', color: '#60a5fa' },
  INCITING:       { letter: 'I', color: '#F5A623' },
  COMPLICATIONS:  { letter: 'P', color: '#fb7185' },
  RESOLUTION:     { letter: 'R', color: '#4ade80' },
};

const FIPRPill = ({ stage }) => {
  const c = FIPR_CONFIG[stage];
  if (!c) return null;
  return (
    <span className="text-[9px] font-black px-1 py-px rounded shrink-0 leading-tight"
          style={{ background: `${c.color}18`, color: c.color, border: `1px solid ${c.color}30` }}>
      {c.letter}
    </span>
  );
};

const STRIP_COLORS = {
  'INT DAY':   { bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-300', pill: 'bg-amber-500/30 text-amber-300', accent: '#f59e0b' },
  'INT NIGHT': { bg: 'bg-indigo-500/20', border: 'border-indigo-500/40', text: 'text-indigo-300', pill: 'bg-indigo-500/30 text-indigo-300', accent: '#6366f1' },
  'EXT DAY':   { bg: 'bg-sky-500/20', border: 'border-sky-500/40', text: 'text-sky-300', pill: 'bg-sky-500/30 text-sky-300', accent: '#0ea5e9' },
  'EXT NIGHT': { bg: 'bg-violet-500/20', border: 'border-violet-500/40', text: 'text-violet-300', pill: 'bg-violet-500/30 text-violet-300', accent: '#8b5cf6' },
  'EXT DAWN':  { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-300', pill: 'bg-orange-500/30 text-orange-300', accent: '#f97316' },
  'EXT DUSK':  { bg: 'bg-rose-500/20', border: 'border-rose-500/40', text: 'text-rose-300', pill: 'bg-rose-500/30 text-rose-300', accent: '#f43f5e' },
};

const getStyle = (scene) => {
  const locType = (scene.location_type || 'INT').toUpperCase();
  const tod = (scene.time_of_day || 'DAY').toUpperCase();
  return STRIP_COLORS[`${locType} ${tod}`] || STRIP_COLORS['INT DAY'];
};

// ── Scene Strip (board view) ──
const SceneStrip = ({ scene, onRemove, draggable, onDragStart, onOpen, fiprStage, onNoteSave }) => {
  const style = getStyle(scene);
  const synopsis = (scene.synopsis_en || scene.synopsis || '').trim();
  const [noteText,  setNoteText]  = useState(scene.strip_notes || '');
  const [noteSaved, setNoteSaved] = useState(false);

  const saveNote = async () => {
    if (noteText === (scene.strip_notes || '')) return;
    await onNoteSave?.(scene.id, noteText);
    setNoteSaved(true);
    setTimeout(() => setNoteSaved(false), 1500);
  };

  return (
    <div
      className={`rounded-lg border ${style.bg} ${style.border} mb-2 group/strip`}
      draggable={draggable}
      onDragStart={onDragStart}
      data-testid={`strip-${scene.scene_number ?? 'unnumbered'}`}
    >
      <div className="flex items-stretch">
        <div className="w-1.5 rounded-l shrink-0" style={{ backgroundColor: style.accent }} />
        <div className="flex-1 p-2.5 min-w-0">
          {/* Header: scene num + pill + location + FIPR + expand icon */}
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className={`text-xs font-bold ${style.text}`}>{formatSceneNumber(scene.scene_number)}</span>
            <span className={`text-[9px] px-1.5 rounded-full font-semibold ${style.pill}`}>
              {(scene.location_type || 'INT')} · {(scene.time_of_day || 'DAY')}
            </span>
            <span className="text-[10px] text-[#a0a0a0] truncate flex-1">{scene.location_name}</span>
            {fiprStage && <FIPRPill stage={fiprStage} />}
            <button
              onClick={e => { e.stopPropagation(); onOpen?.(scene); }}
              className="opacity-0 group-hover/strip:opacity-100 transition-opacity text-[#555] hover:text-[#a0a0a0] flex-shrink-0"
              title="View full scene details"
              data-testid={`expand-scene-${scene.scene_number ?? 'unnumbered'}`}
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="6.5,1 10,1 10,4.5"/>
                <polyline points="4.5,10 1,10 1,6.5"/>
                <line x1="10" y1="1" x2="6" y2="5"/>
                <line x1="1" y1="10" x2="5" y2="6"/>
              </svg>
            </button>
          </div>
          {/* Scene heading — clickable to open detail */}
          <p
            className="text-xs text-[#f0f0f0] font-medium leading-tight mb-1 cursor-pointer hover:text-[#e8c547] transition-colors"
            onClick={() => onOpen?.(scene)}
          >
            {scene.scene_header}
          </p>
          {/* Synopsis */}
          {synopsis && (
            <p className="text-[10px] text-[#777] leading-snug line-clamp-2 mb-1.5 cursor-pointer" onClick={() => onOpen?.(scene)}>
              {synopsis}
            </p>
          )}
          {/* Cast chips */}
          {(scene.characters_in_scene || []).length > 0 && (
            <div className="flex items-center gap-1 flex-wrap mb-1.5">
              {(scene.characters_in_scene || []).slice(0, 4).map(c => (
                <span key={c} className="text-[9px] text-[#666] bg-[#1a1a1a] px-1.5 py-0.5 rounded font-mono">{c}</span>
              ))}
              {(scene.characters_in_scene || []).length > 4 && (
                <span className="text-[9px] text-[#444]">+{scene.characters_in_scene.length - 4}</span>
              )}
            </div>
          )}
          {/* Page count */}
          <p className="text-[10px] text-[#444] mb-1.5">{((scene.page_count_eighths || 0) / 8).toFixed(1)}p</p>
          {/* Strip notes — editable, separate from breakdown notes */}
          <div onClick={e => e.stopPropagation()}>
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              onBlur={saveNote}
              placeholder="Scene notes…"
              rows={2}
              className="w-full text-[10px] text-[#c0c0c0] bg-black/30 border border-[#252525] rounded px-2 py-1 resize-none outline-none focus:border-[#e8c547]/30 placeholder-[#383838] transition-colors leading-relaxed"
              data-testid={`strip-notes-${scene.scene_number ?? 'unnumbered'}`}
            />
            {noteSaved && <span className="text-[9px] text-[#4ade80]">saved</span>}
          </div>
        </div>
        {onRemove && (
          <button
            onClick={e => { e.stopPropagation(); onRemove(scene.id); }}
            className="px-1.5 opacity-0 group-hover/strip:opacity-100 transition-opacity text-[#505050] hover:text-red-400 self-start pt-2.5">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

// ── Shoot Day Card ──
const ShootDayCard = ({ day, allScenes, onUpdate, onDelete, onSceneDrop, onSceneRemove, onDragStart, colWidth, fiprMap, onSceneOpen, onNoteSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ shoot_date: day.shoot_date || '', call_time: day.call_time || '', location: day.location || '', notes: day.notes || '' });
  const [showPicker, setShowPicker] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const totalPages = day.scenes.reduce((s, sc) => s + (sc.page_count_eighths || 0), 0);
  const available = allScenes.filter(sc => !day.scene_ids.includes(sc.id));

  return (
    <div
      className={`flex-shrink-0 bg-[#1a1a1a] border rounded-xl flex flex-col transition-colors ${dragOver ? 'border-[#e8c547] shadow-lg shadow-[#e8c547]/10' : 'border-[#2a2a2a]'}`}
      style={{ width: colWidth }}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); const sid = e.dataTransfer.getData('sceneId'); const from = e.dataTransfer.getData('fromDayId'); if (sid) onSceneDrop(sid, from, day.id); }}
      data-testid={`day-${day.day_number}`}
    >
      <div className="p-3 border-b border-[#2a2a2a]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Film className="h-3.5 w-3.5 text-[#e8c547]" />
            <span className="font-bold text-[#e8c547] text-sm">Day {day.day_number}</span>
          </div>
          <div className="flex gap-0.5">
            <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)} className="h-5 w-5 p-0 text-[#a0a0a0] hover:text-[#e8c547]" data-testid={`edit-day-${day.day_number}`}><Edit2 className="h-3 w-3" /></Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(day.id)} className="h-5 w-5 p-0 text-[#a0a0a0] hover:text-red-400" data-testid={`del-day-${day.day_number}`}><Trash2 className="h-3 w-3" /></Button>
          </div>
        </div>
        {editing ? (
          <div className="space-y-1.5">
            <Input type="date" value={draft.shoot_date} onChange={e => setDraft(p => ({...p, shoot_date: e.target.value}))} className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-6 text-xs" />
            <Input value={draft.call_time} onChange={e => setDraft(p => ({...p, call_time: e.target.value}))} placeholder="Call time" className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-6 text-xs" />
            <Input value={draft.location} onChange={e => setDraft(p => ({...p, location: e.target.value}))} placeholder="Location" className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-6 text-xs" />
            <div className="flex gap-1">
              <Button size="sm" onClick={() => { onUpdate(day.id, draft); setEditing(false); }} className="h-5 px-2 bg-[#e8c547] text-black text-xs"><Save className="h-2.5 w-2.5 mr-0.5" />Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-5 px-2 text-[#a0a0a0] text-xs">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-0.5">
            {day.shoot_date && <div className="flex items-center gap-1 text-xs text-[#a0a0a0]"><Calendar className="h-2.5 w-2.5" />{day.shoot_date}</div>}
            {day.call_time && <div className="flex items-center gap-1 text-xs text-[#a0a0a0]"><Clock className="h-2.5 w-2.5" />Call: {day.call_time}</div>}
            {day.location && <div className="flex items-center gap-1 text-xs text-[#a0a0a0]"><MapPin className="h-2.5 w-2.5" /><span className="truncate">{day.location}</span></div>}
            <div className="flex justify-between mt-1">
              <span className="text-xs text-[#555]">{day.scenes.length} scenes</span>
              <span className="text-xs text-[#e8c547]">{totalPages}/8 pg</span>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 p-2.5 min-h-[100px]">
        {day.scenes.length === 0 && (
          <div className="h-full flex items-center justify-center text-xs text-[#333] border-2 border-dashed border-[#2a2a2a] rounded-lg p-3 text-center">Drop scenes here</div>
        )}
        {day.scenes.map(scene => (
          <SceneStrip key={scene.id} scene={scene}
            onRemove={sid => onSceneRemove(sid, day.id)} draggable
            fiprStage={fiprMap?.[scene.scene_number]}
            onOpen={onSceneOpen}
            onNoteSave={onNoteSave}
            onDragStart={e => { e.dataTransfer.setData('sceneId', scene.id); e.dataTransfer.setData('fromDayId', day.id); onDragStart(scene.id); }} />
        ))}
      </div>
      <div className="p-2.5 border-t border-[#2a2a2a]">
        {showPicker ? (
          <div className="max-h-36 overflow-y-auto space-y-0.5">
            {available.length === 0 ? <p className="text-xs text-[#a0a0a0] text-center py-2">All scheduled</p> : available.map(sc => (
              <button key={sc.id} onClick={() => { onSceneDrop(sc.id, null, day.id); setShowPicker(false); }}
                className="w-full text-left text-xs text-[#f0f0f0] hover:bg-[#2a2a2a] px-2 py-1 rounded flex items-center gap-1.5" data-testid={`pick-${sc.scene_number ?? 'unnumbered'}`}>
                <span className="text-[#e8c547] font-bold w-5">{formatSceneNumber(sc.scene_number)}</span>
                <span className="truncate">{sc.location_name}</span>
              </button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setShowPicker(false)} className="w-full h-5 text-xs text-[#a0a0a0]">Close</Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => setShowPicker(true)} className="w-full h-6 text-xs text-[#a0a0a0] hover:text-[#e8c547]" data-testid={`add-to-day-${day.day_number}`}>
            <Plus className="h-3 w-3 mr-1" /> Add Scene
          </Button>
        )}
      </div>
    </div>
  );
};

// ── Playaround Card (4-per-row grid, drag-and-drop reorderable) ──
const PlayaroundStrip = ({ scene, index, isDragging, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd }) => {
  const style = getStyle(scene);
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, index)}
      onDragOver={e => { e.preventDefault(); onDragOver(index); }}
      onDrop={e => { e.preventDefault(); onDrop(index); }}
      onDragEnd={onDragEnd}
      className={`rounded-lg border p-3 select-none transition-all duration-150 cursor-grab active:cursor-grabbing flex flex-col gap-1.5
        ${isDragging ? 'opacity-40 scale-[0.97]' : ''}
        ${isDragOver ? `${style.border} ring-1 ring-[#e8c547]/60 shadow-lg shadow-[#e8c547]/10` : `${style.bg} ${style.border}`}
      `}
      data-testid={`pa-strip-${scene.scene_number ?? 'unnumbered'}`}
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-bold ${style.text}`}>{formatSceneNumber(scene.scene_number, { prefix: '#' })}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${style.pill}`}>
            {scene.location_type || 'INT'} {scene.time_of_day || 'DAY'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[#555] font-mono">{scene.page_count_eighths || 0}/8</span>
          <GripVertical className="h-3 w-3 text-[#333]" />
        </div>
      </div>
      {/* Scene header */}
      <p className="text-xs text-[#f0f0f0] line-clamp-2 leading-tight">{scene.scene_header}</p>
      {/* Location */}
      <p className="text-[10px] text-[#555] truncate">{scene.location_name}</p>
      {/* Characters */}
      {(scene.characters_in_scene || []).length > 0 && (
        <div className="flex gap-1 flex-wrap mt-0.5">
          {scene.characters_in_scene.slice(0, 3).map(c => (
            <span key={c} className="text-[9px] text-[#555] bg-[#1a1a1a] px-1 rounded">{c}</span>
          ))}
          {scene.characters_in_scene.length > 3 && (
            <span className="text-[9px] text-[#444]">+{scene.characters_in_scene.length - 3}</span>
          )}
        </div>
      )}
      {/* Order indicator */}
      <div className="text-[9px] text-[#333] font-mono mt-auto pt-1 border-t border-[#2a2a2a]">Order: {index + 1}</div>
    </div>
  );
};


export default function StripBoardPage() {
  const { projectId } = useParams();
  const { token } = useAuth();

  const [mode, setMode] = useState('scenes'); // 'scenes' | 'playaround'
  const [expandedSceneId, setExpandedSceneId] = useState(null);
  const [fullBreakdown, setFullBreakdown] = useState([]);
  const [colsPerRow, setColsPerRow] = useState('3');
  // Synopsis language toggle for the UI strip board display (separate from PDF download)
  const [stripSynopsisLang, setStripSynopsisLang] = useState('english'); // 'english' | 'original'
  const [days, setDays] = useState([]);
  const [allScenes, setAllScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [draggingSceneId, setDraggingSceneId] = useState(null);
  const [projectTitle, setProjectTitle] = useState('');
  // Playaround state
  const [playaroundOrder, setPlayaroundOrder] = useState([]);
  const [exportingPA, setExportingPA] = useState(false);
  const [paDraggingIdx, setPaDraggingIdx] = useState(null);
  const [paDragOverIdx, setPaDragOverIdx] = useState(null);
  // Scene detail modal
  const [modalScene, setModalScene] = useState(null);
  // FIPR beat map: scene_number → stage
  const [fiprMap, setFiprMap] = useState({});
  // Print PDF
  const [printingPDF, setPrintingPDF] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [projectId]);

  // Load FIPR beat board
  useEffect(() => {
    if (!projectId || !token) return;
    axios.get(`${API}/projects/${projectId}/beat-board`, { headers })
      .then(({ data }) => {
        if (data.status === 'ready') {
          const map = {};
          // Skip null-numbered beats so they don't all collide on map[null].
          (data.beats || []).forEach(b => {
            if (b.scene_number !== null && b.scene_number !== undefined && b.scene_number !== '') {
              map[b.scene_number] = b.stage;
            }
          });
          setFiprMap(map);
        }
      }).catch(() => {});
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Strip Download state
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [scenesPerPage,      setScenesPerPage]      = useState(2);
  const [downloadLanguage,   setDownloadLanguage]   = useState('english');
  const [includeNotes,       setIncludeNotes]       = useState(true);

  const handleNoteSave = async (sceneId, noteText) => {
    try {
      await axios.patch(`${API}/scenes/${sceneId}/strip-notes`,
        { strip_notes: noteText }, { headers });
      // Update fullBreakdown in place so note persists without re-fetch
      setFullBreakdown(prev => prev.map(s => s.id === sceneId ? { ...s, strip_notes: noteText } : s));
      setAllScenes(prev => prev.map(s => s.id === sceneId ? { ...s, strip_notes: noteText } : s));
    } catch (err) { console.error('strip-board.strip-notes-patch:', err); }
  };

  const handlePrintPDF = async () => {
    setPrintingPDF(true);
    setShowDownloadDialog(false);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/export/stripboard-print?scenes_per_page=${scenesPerPage}&language=${downloadLanguage}&include_notes=${includeNotes ? 1 : 0}`,
        {},
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${projectTitle || 'stripboard'}_strips.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Strip PDF downloaded');
    } catch {
      toast.error('Failed to generate strip PDF');
    } finally {
      setPrintingPDF(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [schedRes, breakdownRes, projRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/schedule`, { headers }),
        axios.get(`${API}/projects/${projectId}/breakdown`, { headers }),
        axios.get(`${API}/projects`, { headers })
      ]);
      const schedDays = schedRes.data;
      setDays(schedDays);
      setFullBreakdown(breakdownRes.data);
      const sceneList = breakdownRes.data.map(s => ({
        id: s.id, scene_number: s.scene_number, scene_header: s.scene_header,
        location_name: s.location_name, location_type: s.location_type,
        time_of_day: s.time_of_day, page_count_eighths: s.page_count_eighths,
        characters_in_scene: s.characters_in_scene
      }));
      setAllScenes(sceneList);
      const proj = projRes.data.find(p => p.id === projectId);
      if (proj) setProjectTitle(proj.title);
      // Build playaround order from schedule order
      const ordered = [];
      schedDays.forEach(day => {
        day.scenes.forEach(sc => {
          const fullScene = sceneList.find(s => s.id === sc.id) || sc;
          ordered.push({ ...fullScene, dayLabel: `Day ${day.day_number}` });
        });
      });
      // Add unscheduled at end
      const scheduledIds = new Set(schedDays.flatMap(d => d.scene_ids));
      sceneList.filter(s => !scheduledIds.has(s.id)).forEach(s => ordered.push({ ...s, dayLabel: 'Unscheduled' }));
      setPlayaroundOrder(ordered);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleAutoGenerate = async () => {
    if (!window.confirm('Auto-generate will replace the current schedule. Continue?')) return;
    setGenerating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/schedule/auto`, {}, { headers });
      toast.success('Schedule generated');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const handleAddDay = async () => {
    const next = (days.length > 0 ? Math.max(...days.map(d => d.day_number)) : 0) + 1;
    try {
      await axios.post(`${API}/projects/${projectId}/schedule`, { day_number: next, scene_ids: [] }, { headers });
      toast.success(`Day ${next} added`);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleUpdateDay = async (dayId, draft) => {
    try { await axios.put(`${API}/projects/${projectId}/schedule/${dayId}`, draft, { headers }); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const handleDeleteDay = async (dayId) => {
    if (!window.confirm('Remove this day?')) return;
    try { await axios.delete(`${API}/projects/${projectId}/schedule/${dayId}`, { headers }); toast.success('Day removed'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const handleSceneDrop = async (sceneId, fromDayId, toDayId) => {
    if (fromDayId === toDayId) return;
    // Optimistic
    const scenesById = {};
    allScenes.forEach(s => scenesById[s.id] = s);
    const updated = days.map(d => {
      if (d.id === fromDayId) return { ...d, scenes: d.scenes.filter(s => s.id !== sceneId), scene_ids: d.scene_ids.filter(id => id !== sceneId) };
      if (d.id === toDayId) {
        const scene = scenesById[sceneId] || days.flatMap(dd => dd.scenes).find(s => s.id === sceneId);
        return scene ? { ...d, scenes: [...d.scenes, scene], scene_ids: [...d.scene_ids, sceneId] } : d;
      }
      return d;
    });
    setDays(updated);
    try {
      if (fromDayId) { const src = days.find(d => d.id === fromDayId); if (src) await axios.put(`${API}/projects/${projectId}/schedule/${fromDayId}`, { scene_ids: src.scene_ids.filter(id => id !== sceneId) }, { headers }); }
      const dest = days.find(d => d.id === toDayId);
      if (dest) await axios.put(`${API}/projects/${projectId}/schedule/${toDayId}`, { scene_ids: [...dest.scene_ids.filter(id => id !== sceneId), sceneId] }, { headers });
    } catch { toast.error('Failed to move'); fetchAll(); }
  };

  const handleSceneRemove = async (sceneId, dayId) => {
    const day = days.find(d => d.id === dayId);
    if (!day) return;
    try { await axios.put(`${API}/projects/${projectId}/schedule/${dayId}`, { scene_ids: day.scene_ids.filter(id => id !== sceneId) }, { headers }); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  // Playaround reorder
  const moveScene = (idx, dir) => {
    const arr = [...playaroundOrder];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setPlayaroundOrder(arr);
  };

  const handlePaDragStart = (e, idx) => {
    setPaDraggingIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handlePaDragOver = (idx) => { if (idx !== paDraggingIdx) setPaDragOverIdx(idx); };
  const handlePaDrop = (targetIdx) => {
    if (paDraggingIdx === null || paDraggingIdx === targetIdx) return;
    const arr = [...playaroundOrder];
    const [removed] = arr.splice(paDraggingIdx, 1);
    arr.splice(targetIdx, 0, removed);
    setPlayaroundOrder(arr);
    setPaDraggingIdx(null);
    setPaDragOverIdx(null);
  };
  const handlePaDragEnd = () => { setPaDraggingIdx(null); setPaDragOverIdx(null); };

  const handleExportPlayaround = async () => {
    setExportingPA(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/export/oneliner-custom`,
        { scene_ids: playaroundOrder.map(s => s.id) },
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `playaround_oneliner.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExportingPA(false); }
  };

  const unscheduled = allScenes.filter(sc => !days.some(d => d.scene_ids.includes(sc.id)));
  const totalPages = days.reduce((sum, d) => sum + d.scenes.reduce((s, sc) => s + (sc.page_count_eighths || 0), 0), 0);
  const cols = parseInt(colsPerRow, 10) || 3;
  const cardWidth = Math.max(220, Math.min(300, Math.floor((window.innerWidth - 208 - 48 - (cols - 1) * 16) / cols)));

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-[#f0f0f0] flex flex-col">
      {/* Header */}
      <div className="border-b border-[#2a2a2a] bg-[#0d0d0d] sticky top-0 z-10 px-5 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <GripVertical className="h-5 w-5 text-[#e8c547]" />
            <div>
              <h1 className="font-['DM_Serif_Display'] text-lg text-[#f0f0f0]">Strip Board</h1>
              <p className="text-xs text-[#a0a0a0]">{projectTitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* View mode toggle */}
            <div className="flex bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-0.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setMode('scenes')}
                className={`h-7 px-3 text-xs ${mode === 'scenes' ? 'bg-[#4a9eff] text-white' : 'text-[#a0a0a0]'}`}
                data-testid="scenes-grid-btn"
              >
                <Grid3X3 className="h-3.5 w-3.5 mr-1" /> Scenes Grid
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setMode('playaround')}
                className={`h-7 px-3 text-xs ${mode === 'playaround' ? 'bg-[#e8c547] text-black' : 'text-[#a0a0a0]'}`}
                data-testid="playaround-btn"
              >
                <GripVertical className="h-3.5 w-3.5 mr-1" /> Playaround
              </Button>
            </div>

            {/* Synopsis language toggle — visible in Scenes Grid mode */}
            {mode === 'scenes' && (
              <div className="flex items-center gap-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-0.5"
                   data-testid="synopsis-lang-toggle">
                <button
                  onClick={() => setStripSynopsisLang('english')}
                  className={`h-7 px-2.5 text-[10px] font-semibold rounded-md transition-colors ${
                    stripSynopsisLang === 'english' ? 'bg-[#2a2a2a] text-[#e8c547]' : 'text-[#505050]'
                  }`}>
                  EN
                </button>
                <button
                  onClick={() => setStripSynopsisLang('original')}
                  className={`h-7 px-2.5 text-[10px] font-semibold rounded-md transition-colors ${
                    stripSynopsisLang === 'original' ? 'bg-[#2a2a2a] text-[#e8c547]' : 'text-[#505050]'
                  }`}>
                  Original
                </button>
              </div>
            )}

            {mode === 'playaround' && (
              <Button size="sm" onClick={handleExportPlayaround} disabled={exportingPA} className="bg-[#e8c547] hover:bg-[#d6b539] text-black h-7" data-testid="export-pa-btn">
                <Download className="h-3.5 w-3.5 mr-1" />
                {exportingPA ? 'Exporting...' : 'Save as One-Liner PDF'}
              </Button>
            )}
            {/* Strip Download button */}
            <Button size="sm" onClick={() => setShowDownloadDialog(true)} disabled={printingPDF}
                    className="h-7 text-xs border border-[#2a2a2a] text-[#a0a0a0] hover:text-white bg-transparent"
                    data-testid="print-stripboard-btn">
              <Download className="h-3.5 w-3.5 mr-1" />
              {printingPDF ? 'Generating…' : 'Strip Download'}
            </Button>
          </div>
        </div>

        {/* Stats + Legend */}
        <div className="flex items-center gap-6 mt-2 flex-wrap">
          <div className="text-xs text-[#a0a0a0]">
            <span className="text-[#f0f0f0] font-medium">{days.length}</span> days ·{' '}
            <span className="text-[#f0f0f0] font-medium">{totalPages}/8</span> pg total
          </div>
          {['INT DAY', 'INT NIGHT', 'EXT DAY', 'EXT NIGHT', 'EXT DAWN', 'EXT DUSK'].map(label => (
            <div key={label} className="flex items-center gap-1">
              <div className="h-2 w-2 rounded-sm" style={{ backgroundColor: STRIP_COLORS[label]?.accent }} />
              <span className="text-xs" style={{ color: STRIP_COLORS[label]?.accent }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#a0a0a0]">Loading strip board...</div>
      ) : mode === 'scenes' ? (
        /* ── SCENES GRID MODE — click → SceneViewModal (same as breakdown) ── */
        <div className="flex-1 overflow-y-auto p-5" data-testid="scenes-grid-view">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-5 items-start">
            {fullBreakdown.map(scene => {
              const itemCount = Object.values(scene.breakdown_items || {}).reduce((s, a) => s + (a?.length || 0), 0);
              const style = getStyle(scene);
              const synopsis = stripSynopsisLang === 'original'
                ? (scene.synopsis || scene.synopsis_en || '').trim()
                : (scene.synopsis_en || scene.synopsis || '').trim();
              const hasNote = !!(scene.strip_notes || '').trim();
              const fiprStage = fiprMap[scene.scene_number];
              const FIPR_COLORS = { FUNNEL: '#60a5fa', INCITING: '#F5A623', COMPLICATIONS: '#fb7185', RESOLUTION: '#4ade80' };
              const FIPR_LETTERS = { FUNNEL: 'F', INCITING: 'I', COMPLICATIONS: 'P', RESOLUTION: 'R' };

              return (
                <SceneGridCard
                  key={scene.id}
                  scene={scene}
                  onOpen={() => setModalScene(scene)}
                  onNoteSave={handleNoteSave}
                  style={style}
                  itemCount={itemCount}
                  synopsis={synopsis}
                  hasNote={hasNote}
                  fiprStage={fiprStage}
                  fiprColor={FIPR_COLORS[fiprStage]}
                  fiprLetter={FIPR_LETTERS[fiprStage]}
                />
              );
            })}
          </div>

          {fullBreakdown.length === 0 && (
            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center">
              <Grid3X3 className="h-10 w-10 text-[#2a2a2a] mx-auto mb-3" />
              <p className="text-[#a0a0a0]">No scenes found. Upload a script first.</p>
            </Card>
          )}
        </div>
      ) : mode === 'playaround' ? (
        /* ── PLAYAROUND MODE ── */
        <div className="flex-1 overflow-y-auto p-5">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-[#f0f0f0]">Playaround Mode</h2>
                <p className="text-xs text-[#a0a0a0]">Drag scenes to reorder. This order will be used for the PDF export. Changes here don't affect the main schedule.</p>
              </div>
              <Badge className="bg-[#e8c547]/20 text-[#e8c547] border-0">{playaroundOrder.length} scenes</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {playaroundOrder.map((scene, idx) => (
                <PlayaroundStrip
                  key={`${scene.id}-${idx}`}
                  scene={scene}
                  index={idx}
                  isDragging={paDraggingIdx === idx}
                  isDragOver={paDragOverIdx === idx}
                  onDragStart={handlePaDragStart}
                  onDragOver={handlePaDragOver}
                  onDrop={handlePaDrop}
                  onDragEnd={handlePaDragEnd}
                />
              ))}
            </div>
            {playaroundOrder.length === 0 && (
              <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center">
                <GripVertical className="h-10 w-10 text-[#2a2a2a] mx-auto mb-3" />
                <p className="text-[#a0a0a0]">No scenes in schedule. Run Auto-Schedule first.</p>
              </Card>
            )}
          </div>
        </div>
      ) : (
        /* ── BOARD VIEW ── */
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-auto p-5">
            {days.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <Film className="h-14 w-14 text-[#2a2a2a]" />
                <p className="text-[#a0a0a0]">No shoot days scheduled yet</p>
                <div className="flex gap-3">
                  <Button onClick={handleAddDay} variant="outline" className="border-[#2a2a2a] text-[#f0f0f0]" data-testid="empty-add-day-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Day Manually
                  </Button>
                  <Button onClick={handleAutoGenerate} disabled={generating} className="bg-[#e8c547] hover:bg-[#d6b539] text-black" data-testid="empty-auto-btn">
                    <Zap className="h-4 w-4 mr-1" /> Auto-Schedule
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="grid gap-4 pb-4"
                style={{ gridTemplateColumns: `repeat(${cols}, ${cardWidth}px)` }}
                data-testid="board-grid"
              >
                {days.map(day => (
                  <ShootDayCard
                    key={day.id}
                    day={day}
                    allScenes={allScenes}
                    onUpdate={handleUpdateDay}
                    onDelete={handleDeleteDay}
                    onSceneDrop={handleSceneDrop}
                    onSceneRemove={handleSceneRemove}
                    onDragStart={setDraggingSceneId}
                    colWidth={cardWidth}
                    fiprMap={fiprMap}
                    onSceneOpen={setModalScene}
                    onNoteSave={handleNoteSave}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Unscheduled panel */}
          {unscheduled.length > 0 && (
            <div className="w-48 shrink-0 border-l border-[#2a2a2a] bg-[#0d0d0d] p-3 overflow-y-auto" data-testid="unscheduled-panel">
              <p className="text-xs font-semibold text-[#a0a0a0] mb-2 uppercase tracking-widest">Unscheduled ({unscheduled.length})</p>
              {unscheduled.map(scene => (
                <SceneStrip key={scene.id} scene={scene} draggable
                  fiprStage={fiprMap[scene.scene_number]}
                  onOpen={setModalScene}
                  onNoteSave={handleNoteSave}
                  onDragStart={e => { e.dataTransfer.setData('sceneId', scene.id); e.dataTransfer.setData('fromDayId', ''); setDraggingSceneId(scene.id); }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scene detail modal — same as breakdown */}
      {modalScene && (
        <SceneViewModal
          scene={modalScene}
          projectId={projectId}
          token={token}
          defaultTab="overview"
          onClose={() => setModalScene(null)}
          onSceneChange={fetchAll}
        />
      )}

      {/* Strip Download dialog — ask scenes per page */}
      {showDownloadDialog && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
             style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
             onClick={e => e.target === e.currentTarget && setShowDownloadDialog(false)}>
          <div className="rounded-2xl border border-[#2a2a2a] p-7 w-full max-w-sm shadow-2xl"
               style={{ background: '#141414' }}>
            <h3 className="text-lg font-bold text-white mb-1">Strip Download</h3>
            <p className="text-sm text-[#a0a0a0] mb-6">How many scenes per A4 page?</p>

            {/* Scene count selector */}
            <div className="grid grid-cols-3 gap-3 mb-7">
              {[1, 2, 3].map(n => (
                <button key={n}
                  onClick={() => setScenesPerPage(n)}
                  className="flex flex-col items-center gap-2 rounded-xl p-4 border transition-all duration-150"
                  style={{
                    borderColor: scenesPerPage === n ? '#e8c547' : '#2a2a2a',
                    background:  scenesPerPage === n ? 'rgba(232,197,71,0.08)' : '#1a1a1a',
                  }}>
                  {/* Mini A4 preview */}
                  <div className="w-10 rounded-sm overflow-hidden border border-[#3a3a3a] flex flex-col gap-0.5 p-1"
                       style={{ height: n === 1 ? 52 : n === 2 ? 52 : 52, background: '#f5f2ec' }}>
                    {Array.from({ length: n }).map((_, i) => (
                      <div key={i} className="rounded-sm flex-1"
                           style={{ background: '#1a1a1a', minHeight: n === 1 ? 38 : n === 2 ? 17 : 10 }} />
                    ))}
                  </div>
                  <span className="text-sm font-semibold"
                        style={{ color: scenesPerPage === n ? '#e8c547' : '#a0a0a0' }}>
                    {n} {n === 1 ? 'scene' : 'scenes'}
                  </span>
                  <span className="text-[10px] text-[#555]">
                    {n === 1 ? 'Most detail' : n === 2 ? 'Balanced' : 'Compact'}
                  </span>
                </button>
              ))}
            </div>

            {/* Language option */}
            <div className="mb-5">
              <p className="text-xs text-[#707070] mb-2">Synopsis language in PDF:</p>
              <div className="flex gap-2">
                {[
                  { val: 'english',  label: 'English',           desc: 'Use English synopsis' },
                  { val: 'original', label: 'Original Language',  desc: 'Use original script language' },
                ].map(opt => (
                  <button key={opt.val}
                    onClick={() => setDownloadLanguage(opt.val)}
                    className="flex-1 text-left rounded-lg border p-2.5 transition-all text-xs"
                    style={{
                      borderColor: downloadLanguage === opt.val ? '#e8c547' : '#2a2a2a',
                      background:  downloadLanguage === opt.val ? 'rgba(232,197,71,0.08)' : '#1a1a1a',
                      color:       downloadLanguage === opt.val ? '#e8c547' : '#a0a0a0',
                    }}>
                    <div className="font-semibold mb-0.5">{opt.label}</div>
                    <div className="text-[9px] opacity-60">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Notes toggle */}
            <div className="mb-5 flex items-center justify-between py-3 px-3 rounded-lg border border-[#2a2a2a]"
                 style={{ background: '#1a1a1a' }}>
              <div>
                <div className="text-xs font-semibold text-[#e0e0e0]">Include notes in PDF</div>
                <div className="text-[10px] text-[#555] mt-0.5">Show NOTES section with written notes</div>
              </div>
              <button
                onClick={() => setIncludeNotes(n => !n)}
                className="relative h-6 w-11 rounded-full transition-colors duration-200 flex-shrink-0"
                style={{ background: includeNotes ? '#e8c547' : '#2a2a2a' }}>
                <span className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200"
                      style={{ transform: includeNotes ? 'translateX(20px)' : 'translateX(0px)' }} />
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDownloadDialog(false)}
                className="flex-1 h-10 rounded-lg text-sm font-medium border border-[#2a2a2a] text-[#a0a0a0] hover:text-white transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePrintPDF}
                disabled={printingPDF}
                className="flex-1 h-10 rounded-lg text-sm font-bold flex items-center justify-center gap-2"
                style={{ background: '#e8c547', color: '#000' }}>
                <Download className="h-4 w-4" />
                {printingPDF ? 'Generating…' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
